<template>
  <div class="credit-management">
    <div class="header">
      <h2>积分管理</h2>
      <div class="actions">
        <el-button type="primary" @click="handleAddCredit">
          <el-icon><Plus /></el-icon>
          发放积分
        </el-button>
        <el-button @click="refreshData">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
      </div>
    </div>

    <!-- 积分列表 -->
    <el-card>
      <el-table :data="creditList" v-loading="loading" stripe border>
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column label="用户" width="120">
          <template #default="{ row }">
            {{ row.user?.realName || row.user?.mobile || '用户' + row.userId }}
          </template>
        </el-table-column>
        <el-table-column label="积分类型" width="120">
          <template #default="{ row }">
            <el-tag>{{ row.creditType?.name || '未知类型' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="余额" width="120">
          <template #default="{ row }">
            {{ row.balance }} 积分
          </template>
        </el-table-column>
        <el-table-column label="冻结金额" width="120">
          <template #default="{ row }">
            {{ row.frozenBalance || 0 }} 积分
          </template>
        </el-table-column>
        <el-table-column label="累计获得" width="120">
          <template #default="{ row }">
            {{ row.totalIncome || 0 }} 积分
          </template>
        </el-table-column>
        <el-table-column label="累计消耗" width="120">
          <template #default="{ row }">
            {{ row.totalExpense || 0 }} 积分
          </template>
        </el-table-column>
        <el-table-column label="最后更新" width="180">
          <template #default="{ row }">
            {{ formatDateTime(row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleView(row)">
              详情
            </el-button>
            <el-button type="warning" size="small" @click="handleAdjust(row)">
              调整
            </el-button>
            <el-button 
              v-if="row.frozenBalance > 0" 
              type="success" 
              size="small" 
              @click="handleUnfreeze(row)"
            >
              解冻
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 积分详情对话框 -->
    <el-dialog
      v-model="detailDialogVisible"
      title="积分详情"
      width="600px"
    >
      <el-descriptions :column="2" border v-if="currentDetail">
        <el-descriptions-item label="记录ID">{{ currentDetail.id }}</el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ formatDateTime(currentDetail.createdAt) }}</el-descriptions-item>
        
        <el-descriptions-item label="用户姓名">{{ currentDetail.user?.realName || '-' }}</el-descriptions-item>
        <el-descriptions-item label="手机号">{{ currentDetail.user?.mobile || '-' }}</el-descriptions-item>
        <el-descriptions-item label="用户状态">
          <el-tag :type="currentDetail.user?.status === 'active' ? 'success' : 'danger'">
            {{ currentDetail.user?.status === 'active' ? '正常' : '禁用' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="用户角色">{{ getRoleText(currentDetail.user?.role) }}</el-descriptions-item>
        
        <el-descriptions-item label="积分类型">
          <el-tag>{{ currentDetail.creditType?.name || '-' }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="兑换比例">{{ currentDetail.creditType?.exchangeRate || '-' }}:1</el-descriptions-item>
        
        <el-descriptions-item label="当前余额">
          <span class="balance-text">{{ currentDetail.balance }} 积分</span>
        </el-descriptions-item>
        <el-descriptions-item label="冻结金额">
          <span class="frozen-text">{{ currentDetail.frozenBalance || 0 }} 积分</span>
        </el-descriptions-item>
        <el-descriptions-item label="累计获得">{{ currentDetail.totalIncome || 0 }} 积分</el-descriptions-item>
        <el-descriptions-item label="累计消耗">{{ currentDetail.totalExpense || 0 }} 积分</el-descriptions-item>
      </el-descriptions>
      <template #footer>
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 发放积分对话框 -->
    <el-dialog
      v-model="addCreditDialogVisible"
      title="发放积分"
      width="500px"
      :before-close="handleAddCreditDialogClose"
    >
      <el-form
        ref="addCreditFormRef"
        :model="addCreditForm"
        :rules="addCreditRules"
        label-width="100px"
      >
        <el-form-item label="用户ID" prop="userId">
          <el-input
            v-model="addCreditForm.userId"
            placeholder="请输入用户ID"
            clearable
          />
        </el-form-item>
        <el-form-item label="积分类型" prop="creditTypeId">
          <el-select
            v-model="addCreditForm.creditTypeId"
            placeholder="请选择积分类型"
            clearable
            style="width: 100%"
          >
            <el-option label="帮扶分" value="1" />
            <!-- 消费分已废弃 -->
            <!-- 任务分已废弃 -->
            <el-option label="解债分" value="5" />
            <el-option label="现金分" value="6" />
          </el-select>
        </el-form-item>
        <el-form-item label="金额" prop="amount">
          <el-input
            v-model="addCreditForm.amount"
            placeholder="请输入发放金额"
            clearable
          >
            <template #append>积分</template>
          </el-input>
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input
            v-model="addCreditForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入发放描述（可选）"
            maxlength="200"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="addCreditDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitAddCredit" :loading="addCreditSubmitting">
            确定发放
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Refresh } from '@element-plus/icons-vue'
import { creditApi } from '@/api/modules/credit.js'

const loading = ref(false)
const creditList = ref([])
const pagination = ref({
  current: 1,
  size: 10,
  total: 0
})

// 详情对话框
const detailDialogVisible = ref(false)
const currentDetail = ref(null)

// 发放积分相关状态
const addCreditDialogVisible = ref(false)
const addCreditFormRef = ref()
const addCreditSubmitting = ref(false)
const addCreditForm = reactive({
  userId: '',
  creditTypeId: '',
  amount: '',
  description: ''
})

// 表单验证规则
const addCreditRules = {
  userId: [
    { required: true, message: '请输入用户ID', trigger: 'blur' },
    { pattern: /^\d+$/, message: '用户ID必须为数字', trigger: 'blur' }
  ],
  creditTypeId: [
    { required: true, message: '请选择积分类型', trigger: 'change' }
  ],
  amount: [
    { required: true, message: '请输入发放金额', trigger: 'blur' },
    { pattern: /^\d+(\.\d{1,2})?$/, message: '金额必须为数字，最多两位小数', trigger: 'blur' },
    { validator: (rule, value, callback) => {
      if (value && parseFloat(value) <= 0) {
        callback(new Error('金额必须大于0'))
      } else {
        callback()
      }
    }, trigger: 'blur' }
  ]
}

// 获取积分列表
const fetchCredits = async () => {
  try {
    loading.value = true
    const response = await creditApi.getUserCredits({
      page: pagination.value.current,
      size: pagination.value.size
    })
    if (response.code === 200 && response.data) {
      creditList.value = response.data.list
      pagination.value.total = response.data.total
    } else {
      console.error('获取积分列表失败:', response.message)
      if (response.code === 401) {
        localStorage.removeItem('admin-token')
        localStorage.removeItem('admin-info')
        window.location.href = '/#/login'
        return
      } else {
        ElMessage.error(response.message || '获取积分列表失败')
      }
    }
  } catch (error) {
    console.error('获取积分列表异常:', error)
    ElMessage.error('获取积分列表异常')
  } finally {
    loading.value = false
  }
}

const formatDateTime = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleString('zh-CN')
}

const getRoleText = (role) => {
  const map = {
    debtor: '债务人',
    creditor: '债权人',
    merchant: '商户',
    admin: '管理员',
    normal: '普通用户'
  }
  return map[role] || role
}

const refreshData = () => {
  fetchCredits()
}

// 查看详情
const handleView = (row) => {
  currentDetail.value = row
  detailDialogVisible.value = true
}

// 发放积分功能
const handleAddCredit = () => {
  addCreditDialogVisible.value = true
}

const handleAddCreditDialogClose = (done) => {
  if (addCreditSubmitting.value) {
    ElMessage.warning('正在提交中，请稍候')
    return
  }
  ElMessageBox.confirm('确定要关闭吗？未保存的数据将丢失', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(() => {
    resetAddCreditForm()
    done()
  }).catch(() => {})
}

const resetAddCreditForm = () => {
  addCreditForm.userId = ''
  addCreditForm.creditTypeId = ''
  addCreditForm.amount = ''
  addCreditForm.description = ''
  if (addCreditFormRef.value) {
    addCreditFormRef.value.clearValidate()
  }
}

const submitAddCredit = async () => {
  if (!addCreditFormRef.value) return
  
  try {
    const valid = await addCreditFormRef.value.validate()
    if (!valid) return
    
    addCreditSubmitting.value = true
    
    const formData = {
      userId: parseInt(addCreditForm.userId),
      creditTypeId: parseInt(addCreditForm.creditTypeId),
      amount: parseFloat(addCreditForm.amount),
      description: addCreditForm.description || '管理员发放积分',
      transactionType: 'grant',
      status: 'completed'
    }
    
    const response = await creditApi.createCreditTransaction(formData)
    
    if (response.code === 200 || response.code === 201) {
      ElMessage.success('积分发放成功')
      addCreditDialogVisible.value = false
      resetAddCreditForm()
      refreshData()
    } else {
      ElMessage.error(response.message || '积分发放失败')
    }
  } catch (error) {
    console.error('发放积分失败:', error)
    ElMessage.error('发放积分失败：' + (error.message || '未知错误'))
  } finally {
    addCreditSubmitting.value = false
  }
}

const handleAdjust = (row) => ElMessage.info(`调整积分功能开发中`)
const handleUnfreeze = (row) => {
  row.frozenBalance = 0
  ElMessage.success('积分已解冻')
}

onMounted(() => refreshData())
</script>

<style scoped>
.credit-management { padding: 20px; }
.header { 
  display: flex; 
  justify-content: space-between; 
  align-items: center; 
  margin-bottom: 20px; 
}
.header h2 { margin: 0; font-size: 24px; color: #ffffff; }
.actions { display: flex; gap: 10px; }
.balance-text { font-size: 18px; font-weight: bold; color: #67C23A; }
.frozen-text { font-size: 16px; color: #E6A23C; }
</style>
